import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { supabase } from '../lib/supabase';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const RESEND_COOLDOWN = 60;

const ConfirmEmail: React.FC = () => {
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email ?? '';

  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [fallbackEmail, setFallbackEmail] = useState('');

  const targetEmail = email || fallbackEmail;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (!targetEmail || cooldown > 0) return;

    setResending(true);
    setResendStatus('idle');
    setResendError('');

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: targetEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/email-verified`,
      },
    });

    setResending(false);

    if (error) {
      setResendStatus('error');
      setResendError(error.message);
    } else {
      setResendStatus('sent');
      setCooldown(RESEND_COOLDOWN);
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white px-6 py-16">
        {/* Blueprint grid */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="relative z-10 w-full max-w-sm"
        >
          {/* Header */}
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
              Steelman
            </span>
            <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
              Check your email.
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              One click left before your first debate.
            </p>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 text-center shadow-frame backdrop-blur-sm">
            {/* Envelope mark */}
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-black/[0.08] bg-charcoal/[0.04]">
              <svg
                className="h-5 w-5 text-charcoal"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.75}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>

            <p className="text-sm leading-relaxed text-ink-muted">
              We sent a verification link to
              {email ? (
                <>
                  {' '}
                  <span className="font-mono text-[13px] font-medium text-charcoal">{email}</span>.
                </>
              ) : (
                ' your inbox.'
              )}{' '}
              Click it to activate your account.
            </p>

            <div className="my-6 h-px w-full bg-hairline" />

            {/* Fallback email input when arriving without state */}
            {!email && (
              <div className="mb-4 text-left">
                <label
                  htmlFor="fallback-email"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted"
                >
                  Your email
                </label>
                <input
                  type="email"
                  id="fallback-email"
                  value={fallbackEmail}
                  onChange={e => setFallbackEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10"
                />
              </div>
            )}

            <AnimatePresence>
              {resendStatus === 'sent' && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: EASE }}
                  className="mb-3 overflow-hidden text-sm font-medium text-charcoal"
                >
                  A new link is on its way.
                </motion.p>
              )}
              {resendStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: EASE }}
                  className="mb-3 overflow-hidden rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-4 py-3 text-left text-sm text-charcoal"
                >
                  {resendError || 'Could not resend the email. Please try again shortly.'}
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="button"
              onClick={handleResend}
              disabled={resending || cooldown > 0 || !targetEmail}
              className="btn-secondary w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {resending
                ? 'Resending…'
                : cooldown > 0
                ? `Resend available in ${cooldown}s`
                : 'Resend verification email'}
            </button>

            <p className="mt-4 text-xs text-ink-muted">
              Nothing arriving? Check your spam folder — it happens to the best arguments.
            </p>
          </div>

          {/* Footer link */}
          <p className="mt-6 text-center text-sm text-ink-muted">
            Already verified?{' '}
            <Link
              to="/signin"
              className="font-semibold text-charcoal transition-colors hover:text-ink-muted"
            >
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </MotionConfig>
  );
};

export default ConfirmEmail;
